import { ERRORS, HOSTS, ON_HOSTS, plural, statusColor, statusLabel } from '../format';
import { useApp } from '../store';
import { btn, divider, ellipsis, mono, panelCol, pill, pillGroup, primary, sectionTitle } from '../ui';

const PRESETS: Array<[string, string]> = [
  ['gpupdate /force', 'gpupdate /force'],
  ['перезапуск spooler', 'Restart-Service -Name Spooler -Force'],
  ['очистка WinSxS', 'Dism.exe /Online /Cleanup-Image /StartComponentCleanup'],
  [
    'температура ядер',
    'Get-CimInstance -Namespace root/wmi -ClassName MSAcpi_ThermalZoneTemperature | Select CurrentTemperature'
  ],
  ['свободное место', 'Get-PSDrive C | Select Used,Free'],
  ['обновления WSUS', 'Install-WindowsUpdate -AcceptAll -IgnoreReboot'],
  ['сессии RDP', 'quser'],
  ['сброс DNS-кеша', 'ipconfig /flushdns']
];

export function Commands() {
  const app = useApp();
  const staged = app.bulkMode === 'staged';
  const slices = app.waveSlices();
  const currentSlice = slices[app.wave] ?? [];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.05fr 1fr', gap: 14, alignItems: 'start' }}>
      <section style={panelCol}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={sectionTitle}>Скрипт</span>
          <span style={{ flex: 1 }} />
          <div style={pillGroup}>
            <button onClick={() => app.setShell('ps')} style={pill(app.shell === 'ps')}>
              PowerShell
            </button>
            <button onClick={() => app.setShell('cmd')} style={pill(app.shell === 'cmd')}>
              CMD
            </button>
          </div>
        </div>

        <textarea
          className="focusAcc"
          value={app.cmd}
          onChange={e => app.setCmd(e.target.value)}
          spellCheck={false}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            height: 152,
            padding: '14px 16px',
            borderRadius: 'var(--rs)',
            border: '1px solid var(--line)',
            background: 'var(--code)',
            color: 'var(--codeInk)',
            ...mono,
            fontSize: 12.5,
            lineHeight: 1.65,
            resize: 'vertical',
            outline: 'none'
          }}
        />

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
          {PRESETS.map(([label, command]) => (
            <button
              key={label}
              className="hovPreset"
              onClick={() => app.setCmd(command)}
              title={command}
              style={{
                ...btn,
                padding: '6px 11px',
                borderRadius: 99,
                border: '1px solid var(--line)',
                ...mono,
                fontSize: 11,
                color: 'var(--ink2)'
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div style={divider} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11.5, color: 'var(--ink3)' }}>Режим</span>
          <div style={pillGroup}>
            <button onClick={() => app.setBulkMode('instant')} style={pill(!staged)}>
              Сразу все
            </button>
            <button onClick={() => app.setBulkMode('staged')} style={pill(staged)}>
              Волнами
            </button>
          </div>
        </div>

        {staged && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 9,
              padding: '13px 15px',
              borderRadius: 'var(--rs)',
              background: 'var(--panel2)'
            }}
          >
            {slices.map((slice, i) => {
              const color = i === app.wave ? 'var(--acc)' : i < app.wave ? 'var(--ok)' : 'var(--ink4)';
              return (
                <div
                  key={i}
                  style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto', gap: 12, alignItems: 'center' }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color }}>Волна {i + 1}</span>
                  <span style={{ ...mono, fontSize: 11, color: 'var(--ink3)' }}>
                    {plural(slice.length, HOSTS)} · {['5% канареек', '25% парка', 'остальные'][i]}
                  </span>
                  <span style={{ fontSize: 11, color }}>
                    {i < app.wave ? 'пройдена' : i === app.wave ? 'следующая' : 'ждёт гейта'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <button
            className="bright"
            onClick={() => void app.runCommand()}
            disabled={!app.selCount}
            style={primary}
          >
            {staged
              ? `Запустить волну ${app.wave + 1} · ${plural(currentSlice.length, HOSTS)}`
              : `Запустить на ${plural(app.selCount, ON_HOSTS)}`}
          </button>
          <span style={{ fontSize: 11.5, color: 'var(--ink3)', maxWidth: 260 }}>
            {staged
              ? 'Следующая волна открывается после 100% успеха предыдущей.'
              : 'Запуск без подтверждения. Отменить можно, пока прогон идёт.'}
          </span>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, ...mono, fontSize: 11, color: 'var(--ink3)' }}>
          <span>параллельно {app.runtime.parallel}</span>
          <span>таймаут {app.runtime.timeoutSec} с</span>
          <span>{app.runtime.rdpAccount}</span>
        </div>
      </section>

      <RunLog />
    </div>
  );
}

/** Живой лог прогона: по строке на хост. */
export function RunLog() {
  const app = useApp();
  const job = app.job;

  const ok = job?.targets.filter(t => t.status === 'done').length ?? 0;
  const err = job?.targets.filter(t => t.status === 'error').length ?? 0;
  const total = job?.targets.length ?? 0;

  return (
    <section style={panelCol}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <span style={sectionTitle}>{job ? `Лог · ${plural(total, HOSTS)}` : 'Лог выполнения'}</span>
        <span style={{ ...mono, fontSize: 11, color: 'var(--ink3)' }}>
          {job ? `${ok} готово · ${plural(err, ERRORS)} · ${total - ok - err} в работе` : '—'}
        </span>
      </div>

      {job ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span
            style={{
              display: 'flex',
              height: 6,
              borderRadius: 99,
              overflow: 'hidden',
              background: 'var(--track)'
            }}
          >
            <span
              style={{
                display: 'block',
                height: 6,
                background: 'var(--ok)',
                width: total ? Math.round((ok / total) * 100) + '%' : '0%'
              }}
            />
            <span
              style={{
                display: 'block',
                height: 6,
                background: 'var(--bad)',
                width: total ? Math.round((err / total) * 100) + '%' : '0%'
              }}
            />
          </span>

          <div style={{ maxHeight: 520, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            {job.targets.map(t => (
              <div
                key={t.hostId}
                title={t.stdout || t.stderr || undefined}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '158px 74px 1fr 56px',
                  gap: 12,
                  padding: '9px 0',
                  borderBottom: '1px solid var(--line)',
                  ...mono,
                  fontSize: 11.5,
                  alignItems: 'baseline'
                }}
              >
                <span>{t.name}</span>
                <span style={{ color: statusColor(t.status) }}>{statusLabel(t.status)}</span>
                <span style={{ color: 'var(--ink3)', ...ellipsis }}>
                  {t.out || (t.status === 'running' ? 'выполняется…' : 'в очереди')}
                </span>
                <span style={{ textAlign: 'right', color: 'var(--ink3)' }}>{t.ms ? t.ms + 'мс' : ''}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          style={{
            padding: '40px 18px',
            borderRadius: 'var(--rs)',
            background: 'var(--panel2)',
            fontSize: 12.5,
            color: 'var(--ink3)'
          }}
        >
          Выберите хосты на карте парка — здесь появится живой лог по каждому.
        </div>
      )}
    </section>
  );
}
